import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import {
    LuUsers, LuArrowLeft, LuTrash2, LuShield, LuShieldCheck, LuMail, LuCalendar, LuBot
} from "react-icons/lu";
import { API } from "../App";

const UsersAdminPage = ({ auth }) => {
    const navigate = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Basic security check: if user is not admin and has account info, redirect
        if (auth.user && auth.user.role !== "admin") {
            toast.error("Acceso denegado: Se requieren privilegios de administrador");
            navigate("/dashboard");
            return;
        }
        fetchUsers();
    }, [auth.user]);

    const fetchUsers = async () => {
        try {
            const response = await fetch(`${API}/admin/users`, {
                headers: { Authorization: `Bearer ${auth.token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setUsers(data);
            } else {
                toast.error("Error al cargar usuarios");
            }
        } catch (error) {
            toast.error("Error de conexión");
        } finally {
            setLoading(false);
        }
    };

    const handleToggleRole = async (user) => {
        const newRole = user.role === "admin" ? "user" : "admin";
        if (!window.confirm(`¿Cambiar el rol de ${user.name} a ${newRole}?`)) return;

        try {
            const response = await fetch(`${API}/admin/users/${user.id}/role?role=${newRole}`, {
                method: "PUT",
                headers: { Authorization: `Bearer ${auth.token}` }
            });

            if (response.ok) {
                toast.success(`Usuario actualizado a ${newRole}`);
                setUsers(users.map(u => u.id === user.id ? { ...u, role: newRole } : u));
            } else {
                toast.error("No se pudo actualizar el rol");
            }
        } catch (error) {
            toast.error("Error de conexión");
        }
    };

    const handleDeleteUser = async (user) => {
        if (user.id === auth.user.id) {
            toast.error("No puedes eliminarte a ti mismo");
            return;
        }

        if (!window.confirm(`¿ELIMINAR COMPLETAMENTE a ${user.name}? Esta acción borrará todos sus agentes y datos.`)) return;

        try {
            const response = await fetch(`${API}/admin/users/${user.id}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${auth.token}` }
            });

            if (response.ok) {
                toast.success("Usuario y datos eliminados");
                setUsers(users.filter(u => u.id !== user.id));
            } else {
                toast.error("Error al eliminar usuario");
            }
        } catch (error) {
            toast.error("Error de conexión");
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 glass border-b border-white/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
                    <Link to="/dashboard">
                        <Button variant="ghost" size="icon" className="rounded-xl">
                            <LuArrowLeft className="w-5 h-5" />
                        </Button>
                    </Link>
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                            <LuShield className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                            <h1 className="font-heading font-bold text-lg text-foreground">Control de Usuarios</h1>
                            <p className="text-xs text-muted-foreground">Panel de Administración</p>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-8">
                <div className="grid grid-cols-1 gap-6">
                    <Card className="glass-card border-white/10">
                        <CardHeader className="flex flex-row items-center justify-between">
                            <CardTitle className="text-xl flex items-center gap-2">
                                <LuUsers className="w-5 h-5 text-primary" />
                                Listado de Usuarios ({users.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="border-b border-white/5 text-muted-foreground text-sm">
                                            <th className="py-4 px-4 font-medium uppercase tracking-wider">Usuario</th>
                                            <th className="py-4 px-4 font-medium uppercase tracking-wider text-center">Rol</th>
                                            <th className="py-4 px-4 font-medium uppercase tracking-wider">Registro</th>
                                            <th className="py-4 px-4 font-medium uppercase tracking-wider">Google</th>
                                            <th className="py-4 px-4 font-medium uppercase tracking-wider text-right">Acciones</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {users.map((user) => (
                                            <tr key={user.id} className="border-b border-white/5 hover:bg-white/5 transition-colors group">
                                                <td className="py-4 px-4">
                                                    <div className="flex flex-col">
                                                        <span className="font-medium text-foreground">{user.name}</span>
                                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                                            <LuMail className="w-3 h-3" />
                                                            {user.email}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-4 px-4 text-center">
                                                    <Badge
                                                        variant={user.role === "admin" ? "default" : "secondary"}
                                                        className={user.role === "admin" ? "bg-primary glow-primary" : ""}
                                                    >
                                                        {(user.role || "user").toUpperCase()}
                                                    </Badge>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <span className="text-sm text-muted-foreground">
                                                        {new Date(user.created_at).toLocaleDateString()}
                                                    </span>
                                                </td>
                                                <td className="py-4 px-4">
                                                    <Badge variant="outline" className={user.google_calendar_connected ? "border-green-500/30 text-green-400" : "opacity-30"}>
                                                        {user.google_calendar_connected ? "Conectado" : "No"}
                                                    </Badge>
                                                </td>
                                                <td className="py-4 px-4 text-right">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="rounded-xl hover:bg-primary/20"
                                                            onClick={() => handleToggleRole(user)}
                                                            title={user.role === "admin" ? "Quitar Admin" : "Hacer Admin"}
                                                        >
                                                            {user.role === "admin" ? <LuShieldCheck className="w-4 h-4 text-primary" /> : <LuShield className="w-4 h-4" />}
                                                        </Button>
                                                        <Button
                                                            variant="ghost"
                                                            size="icon"
                                                            className="rounded-xl hover:bg-destructive/20 text-muted-foreground hover:text-destructive"
                                                            onClick={() => handleDeleteUser(user)}
                                                            title="Eliminar Usuario"
                                                        >
                                                            <LuTrash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </main>
        </div>
    );
};

export default UsersAdminPage;
